import { fetchPopularAirports } from 'actions/FlightActions'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import type { ChangeEventHandler, ComponentProps } from 'react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import OfferAirportOptionsList from './OfferAirportOptionsList'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'

interface Props extends Pick<ComponentProps<typeof DropdownList>, 'open' | 'anchorRef' | 'triggerRef'> {
  onClose: () => void
  onSelectAirport: (airport: App.AirportLocation) => void
  selectedAirportCode?: string
}

function OfferAirportSelectDropdown(props: Props) {
  const {
    open,
    anchorRef,
    triggerRef,
    onClose,
    onSelectAirport,
  } = props

  const inputRef = useRef<HTMLInputElement>(null)

  const dispatch = useAppDispatch()
  const isMobile = useScreenSizeOnly('mobile')
  const intl = useIntl()

  useEffect(() => {
    if (open && !isMobile) {
      inputRef.current?.focus()
    }
  }, [isMobile, open])

  useEffect(() => {
    dispatch(fetchPopularAirports())
  }, [dispatch])

  const [searchPhrase, setSearchPhrase] = useState('')

  const handleSearchPhraseInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setSearchPhrase(event.target.value)
  }, [])

  const title = intl.formatMessage({
    id: 'c5bwlaN+Xi',
    defaultMessage: 'Where will you depart from?',
    description: 'Heading title for airport selection dropdown on the offer detail page',
  })

  const subtitle = intl.formatMessage({
    id: '4VAXWmkiHU',
    defaultMessage: 'Select your preferred airport',
    description: 'Subtitle for airport selection dropdown on the offer detail page',
  })

  const applyButtonText = intl.formatMessage({
    id: 'VnPR5gGxqS',
    defaultMessage: 'Apply',
    description: 'Action button label to apply airport selection on the offer detail page',
  })

  const searchPlaceholder = intl.formatMessage({
    id: 'mqMo/fmGhV',
    defaultMessage: 'Search by city or airport',
    description: 'Placeholder text for airport search input in the flights flow',
  })

  return <DropdownList
    size="M"
    open={open && !isMobile}
    anchorRef={anchorRef}
    triggerRef={triggerRef}
    title={title}
    subtitle={subtitle}
    onClose={onClose}
    primaryActionProps={{
      children: applyButtonText,
      onClick: onClose,
    }}
    headerExtension={<TextInputOnly
      placeholder={searchPlaceholder}
      noValidationMessage
      noValidationSpacing
      onChange={handleSearchPhraseInputChange}
      endIcon={<LineSearchIcon />}
      ref={inputRef}
    />}
  >
    <OfferAirportOptionsList
      searchPhrase={searchPhrase}
      onSelectAirport={onSelectAirport}
    />
  </DropdownList>
}

export default React.memo(OfferAirportSelectDropdown)
